import { useMemo } from 'react';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslate } from 'src/locales';
import { ByzStatusBadgeProps } from './types';
import { StyledRoot, StyledDot } from './styles';
import { getBadgeConfig } from './utils';
export * from './types';

const parseDelphiColor = (color?: string): string | null => {
    if (!color) return null;
    const lowerColor = color.toLowerCase();

    if (lowerColor.startsWith('cl')) {
        const colorMap: Record<string, string> = {
            clred: '#FF0000',
            clgreen: '#008000',
            clblue: '#0000FF',
            clblack: '#000000',
            clwhite: '#FFFFFF',
            clgray: '#808080',
            clmaroon: '#800000',
            clolive: '#808000',
            clnavy: '#000080',
            clpurple: '#800080',
            clteal: '#008080',
            clsilver: '#C0C0C0',
            cllime: '#00FF00',
            claqua: '#00FFFF',
            clfuchsia: '#FF00FF',
            clyellow: '#FFFF00',
        };
        return colorMap[lowerColor] || color.replace('cl', '').toLowerCase();
    }

    if (color.startsWith('$')) {
        let hex = color.substring(1);
        if (hex.length > 6) hex = hex.substring(hex.length - 6);
        if (hex.length === 6) {
            const b = hex.substring(0, 2);
            const g = hex.substring(2, 4);
            const r = hex.substring(4, 6);
            return `#${r}${g}${b}`;
        }
    }

    return color;
};

export default function ByzStatusBadge({
    type,
    value,
    label,
    tooltip,
    customColor,
    sx,
    ...other
}: ByzStatusBadgeProps) {
    const { t } = useTranslate();

    const valStr = useMemo(() => (value || '').toString().toLowerCase(), [value]);

    const config = useMemo(
        () => getBadgeConfig(type, valStr, t, label),
        [type, valStr, label, t]
    );

    const parsedCustomColor = useMemo(() => parseDelphiColor(customColor), [customColor]);

    if (!config) {
        return null;
    }

    const content = (
        <StyledRoot
            sx={{
                backgroundColor: parsedCustomColor ? alpha(parsedCustomColor, 0.1) : config.bgColor,
                border: `1px solid ${alpha(parsedCustomColor || config.color, 0.2)}`,
                color: parsedCustomColor || config.color,
                ...sx,
            }}
            {...other}
        >
            <StyledDot sx={{ backgroundColor: parsedCustomColor || config.dotColor }} />

            <Typography
                variant="subtitle2"
                component="span"
                sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: 1,
                    textTransform: 'capitalize',
                }}
            >
                {config.label}
            </Typography>
        </StyledRoot>
    );

    if (tooltip) {
        return <Tooltip title={tooltip}>{content}</Tooltip>;
    }

    return content;
}
